import React, { useEffect, useMemo, useState } from "react";
import LmsTable from "../ui/LmsTable";
import { useHistory } from "react-router-dom";
import {
  getDropdownSemanticOptionsFromData,
  useFetchData,
  useParseRouteActions,
} from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { propertyUnitTableColumnsDictionary } from "../Loans/LmsLoanApplications";
import { submitValidationIsLoanLost } from "../ui/LmsSharedTables";
import { guid } from "../../../Helpers";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { appendParamsToUrl } from "./LmsServicingAmendments";

type UnitFormType = "add" | "edit" | "view";

const BASE_PATH = "/servicing-units";
const LOANS_URL = "loans/list-borrower-loans";

enum ACTION_TEXTS {
  PREVIOUS = "Previous",
  SAVE_PREVIOUS = "Save and Previous",
  NEXT = "Next",
  SAVE_NEXT = "Save and Next",
}

const ACTIONS_GROUP = {
  PREVIOUS: [ACTION_TEXTS.PREVIOUS, ACTION_TEXTS.SAVE_PREVIOUS],
  NEXT: [ACTION_TEXTS.NEXT, ACTION_TEXTS.SAVE_NEXT],
};

const getSaveButton = (text: string, icon?: string) => ({
  text,
  action: "save",
  icon: icon || GlobalConstants.Icons.SAVE,
});

const EDIT_PREV_NEXT_ACTIONS = [
  getSaveButton(ACTION_TEXTS.PREVIOUS, GlobalConstants.Icons.CHEVRON_LEFT),
  getSaveButton(ACTION_TEXTS.NEXT, GlobalConstants.Icons.CHEVRON_RIGHT),
  "save",
  getSaveButton(ACTION_TEXTS.SAVE_PREVIOUS),
  getSaveButton(ACTION_TEXTS.SAVE_NEXT),
  "save-close",
  "delete",
  "close",
];

const PARAMS = {
  LOAN_PROPERTY_ID: "loanPropertyId",
  COPY_UNIT_ID: "copyUnitId",
};

const isDisabledByInactiveStatus = ({ inputs }) => !!inputs.isPaidOffInactiveStatus;
const getUnitFormActions = (
  type: UnitFormType,
  currentUnitIdx: null | number,
  numberOfUnits: number,
  payoff?: boolean,
  loanSubStatus = ""
) => {
  const isLost = loanSubStatus === "Lost";
  switch (type) {
    case "add":
      return ["save-new", "save-continue", "save-close", "close"];
    case "edit": {
      const actionsToRemove = [
        [isLost || payoff, ["delete"]],
        [isLost, ["save", "save-close"]],
        [currentUnitIdx === 0, ACTIONS_GROUP.PREVIOUS],
        [payoff, [...ACTIONS_GROUP.PREVIOUS, ...ACTIONS_GROUP.NEXT]],
        [(currentUnitIdx || 0) + 1 === numberOfUnits, ACTIONS_GROUP.NEXT],
      ].reduce<string[]>(
        (acc, [condition, actions]) => (condition ? [...acc, ...(actions as string[])] : acc),
        []
      );

      return actionsToRemove.length > 0
        ? EDIT_PREV_NEXT_ACTIONS.filter((action) => {
            const actionText = typeof action === "string" ? action : action.text;
            return !actionsToRemove.includes(actionText);
          })
        : EDIT_PREV_NEXT_ACTIONS;
    }
    default:
      return ["close"];
  }
};

const isHiddenCollateralProperty = ({ inputs }) => inputs.collateralProperty;

const allocatedCostsFields = (myForm: BCFormController) => {
  myForm.addCheckboxField("Include With Unit Payoff", { inlineEdit: true });
  return myForm;
};

export const loanPropertyUnitReconveyanceFields = (myForm: BCFormController) => {
  myForm.addGroupFields([
    myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", {
      name: "borrowerOrganizationId",
      disabled: true,
    }),
    myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", {
      disabled: true,
    }),
    myForm.getDisplayField("Loan #", { name: "loanNumberDisplay" }),
    myForm.getDropdownField("Property Type", "metadata/list-property-types", {
      disabled: true,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getDropdownField("Future Property Type", "metadata/list-property-types", {
      tableName: "futurePropertyType",
      tableValueName: "futurePropertyTypeId",
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getDropdownField("Loan Property", "loandetails/list-loan-properties", {
      disabled: true,
      dependencyField: "loanId",
      required: true,
    }),
    myForm.getInputField("Unit #", {
      required: true,
      name: "unitNumber",
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getCheckboxField("Override Partial Deed Release Amount", {
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Partial Deed Release Amount", {
      isDisabledOnFunc: ({ inputs }) =>
        !inputs.overridePartialDeedReleaseAmount || isDisabledByInactiveStatus({ inputs }),
    }),
    myForm.getCurrencyField("Underwritten Value", {
      name: "appraisedValue",
      isDisabledOnFunc: isDisabledByInactiveStatus,
      inlineEdit: true,
    }),
    myForm.getCurrencyField("As Repaired Value", {
      isDisabledOnFunc: isDisabledByInactiveStatus,
      name: "estimatedSalesPrice",
      inlineEdit: true,
    }),
    myForm.getDropdownField("Appraisal Type", "metadata/list-appraisal-types", {
      tableName: "appraisalType",
      isDisabledOnFunc: isDisabledByInactiveStatus,
      tableValueName: "appraisalTypeId",
    }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Actual Sales Price", {
      inlineEdit: true,
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getCalendarField("Pre Sale Date", {
      inlineEdit: true,
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getNumberField("Number of Bedrooms", {
      inlineEdit: true,
      name: "numberOfBedroom",
      isHiddenFunc: isHiddenCollateralProperty,
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getNumberField("Number of Bathrooms", {
      inlineEdit: true,
      name: "numberOfBathroom",
      isHiddenFunc: isHiddenCollateralProperty,
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getNumberField("Number Of Car Garage", {
      inlineEdit: true,
      isHiddenFunc: isHiddenCollateralProperty,
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getNumberField("Square Footage", {
      inlineEdit: true,
      isHiddenFunc: isHiddenCollateralProperty,
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getCalendarField("Reconveyance Date", {
      inlineEdit: true,
      name: "unitReconveyanceDate",
    }),
    myForm.getNumberField("Reconveyance Number", {
      inlineEdit: true,
      name: "unitReconveyanceReference",
    }),
  ]);

  myForm.addGroupFields([
    myForm.getTextareaField("Reconveyance Comments", {
      inlineEdit: true,
      name: "unitReconveyanceComments",
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getCheckboxField("Allow Auto Payoff Quote", {
      inlineEdit: true,
      isDisabledOnFunc: isDisabledByInactiveStatus,
    }),
    myForm.getNumberField("Months Before Start", {
      inlineEdit: true,
      isHiddenFunc: () => true,
    }),
    myForm.getNumberField("Work Duration", {
      name: "buildTimeInMonths",
      inlineEdit: true,
      isHiddenFunc: () => true,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getNumberField("Sell Refi Timeframe", {
      name: "timeFrameToSellOrRefinanceInMonths",
      inlineEdit: true,
      isHiddenFunc: () => true,
    }),
    myForm.getNumberField("Month Of First Draw", {
      inlineEdit: true,
      isHiddenFunc: () => true,
    }),
    myForm.getBlankField(),
  ]);

  myForm.addCenteredGroup(
    [
      myForm.getComponent(
        <h5 style={{ color: "red" }}>Changing Underwritten Value will change LTV, CLTV</h5>
      ),
    ],
    {
      isHiddenFunc: ({ inputs }) =>
        inputs.originalAppraisedValue
          ? inputs.originalAppraisedValue === inputs.appraisedValue
          : !(inputs.originalAppraisedValue || inputs.appraisedValue),
    }
  );

  return myForm;
};

export const LoanPropertyUnitReconveyanceForm = ({
  type,
  payoff,
  unitId,
  copyUnitId,
  loanPropertyId,
  handleClose,
  payoffQuoteOptions,
}: {
  type: UnitFormType;
  unitId?: string;
  payoff?: boolean;
  copyUnitId?: string | null;
  loanPropertyId?: string | null;
  handleClose?: any;
  payoffQuoteOptions?: any;
}) => {
  const unitEndpoint = copyUnitId
    ? `loanproperty/units/${copyUnitId}/copy`
    : type === "add"
    ? `loanproperty/${loanPropertyId}/units/initial-data`
    : `loanproperty/units/${unitId}`;

  const submitMethod = type === "add" ? "POST" : "PUT";
  const history = useHistory();
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const currentUnitIdx = useMemo(
    () => (unitId && type === "edit" && !payoff ? unitIds.indexOf(unitId) : null),
    [unitIds, unitId]
  );

  const [{ data: unitData, isLoading, error }] = useFetchData(unitEndpoint);
  const propertyId = loanPropertyId || (unitData ? unitData.loanPropertyId : null);

  const [
    { data: propertyUnitsData, isLoading: isPropertyUnitsLoading, error: propertyUnitsError },
  ] = useFetchData(type === "edit" && propertyId ? `loanproperty/${propertyId}/units` : "");

  const [{ data: propertyData, isLoading: isPropertyLoading, error: propertyError }] = useFetchData(
    propertyId ? `loanproperty/${propertyId}` : ""
  );

  const [{ data: loanStatusData = {}, isLoading: isLoanStatusLoading, error: loanStatusError }] =
    useFetchData(propertyData ? `loans/loan-sub-status/${propertyData.loanId}` : "");

  const data = useMemo(
    () =>
      unitData && propertyData
        ? {
            ...unitData,
            loanPropertyId: propertyId,
            loanId: propertyData.loanId,
            collateralProperty: propertyData.collateralProperty,
            originalAppraisedValue: unitData.appraisedValue,
            originalPartialDeedReleaseAmount: unitData.partialDeedReleaseAmount,
          }
        : {},
    [unitData, propertyData]
  );

  const onUpdatedUnitSuccess = (responseData) => {
    if (payoffQuoteOptions) {
      payoffQuoteOptions.setInputs({
        ...payoffQuoteOptions.inputs,
        lastUpdatedUnitId: responseData?.id || guid(),
      });
    }
  };

  const myForm = loanPropertyUnitReconveyanceFields(
    new BCFormController(data, {
      values: data,
      submitMethod,
      initIsDisabled:
        type === "view" ||
        data.isPaidoff ||
        !!data.convertedToNewPropertyOn ||
        loanStatusData.loanSubStatus === "Lost",
      submitSuffix: "loanproperty/units",
      deleteSuffix: "loanproperty/units",
      clearSuffixesOnSubmit: [
        unitEndpoint,
        `loanproperty/${propertyId}/units`,
        "/loanproperty/units",
      ],
      errorTitle:
        unitData && unitData.convertedToNewPropertyOn
          ? `Unit is converted on ${unitData.convertedToNewPropertyOn}`
          : "",
      saveAndContinueSuffix: `${BASE_PATH}/edit/{id}?${PARAMS.LOAN_PROPERTY_ID}=${propertyId}`,
      dataFormatterFunc: (data) => (type === "add" || copyUnitId ? { ...data, id: guid() } : data),
      ...(payoff ? { onSubmitSuccess: onUpdatedUnitSuccess } : {}),
    })
  );

  const onFormChanged = ({ inputs, setInputs }, lastUpdated) => {
    switch (lastUpdated) {
      case "overridePartialDeedReleaseAmount": {
        if (!inputs.overridePartialDeedReleaseAmount) {
          setInputs((prevInputs) => ({
            ...prevInputs,
            partialDeedReleaseAmount: prevInputs.originalPartialDeedReleaseAmount,
          }));
        }
        break;
      }
    }
  };

  const handleMoveUnit = (buttonText: ACTION_TEXTS) => {
    if (buttonText.includes("Close")) {
      history.goBack();
    }
    let neutral = 0;
    if (ACTIONS_GROUP.PREVIOUS.includes(buttonText)) {
      neutral = -1;
    } else if (ACTIONS_GROUP.NEXT.includes(buttonText)) {
      neutral = 1;
    }
    if (neutral) {
      history.replace(
        `/servicing-units/edit/${unitIds[(currentUnitIdx || 0) + neutral]}?${
          PARAMS.LOAN_PROPERTY_ID
        }=${propertyId}`
      );
    }
  };

  const handleSubmit = (data, options) => {
    const { buttonText, handleSubmit } = options;
    if (buttonText.includes("Save")) {
      handleSubmit("loanproperty/units", {
        data,
        buttonText,
        method: submitMethod,
        onSuccessFunc: () => {
          handleMoveUnit(buttonText);
        },
      });
    } else {
      handleMoveUnit(buttonText);
    }
  };

  useEffect(() => {
    if (propertyUnitsData && propertyUnitsData.length > 0) {
      setUnitIds(propertyUnitsData.map((unit) => unit.id));
    }
  }, [propertyUnitsData]);

  return (
    <>
      <LmsFormGenerator
        myForm={myForm}
        onFormChanged={onFormChanged}
        handleSubmit={currentUnitIdx !== null ? handleSubmit : undefined}
        error={error || propertyError || propertyUnitsError || loanStatusError}
        isLoading={isLoading || isPropertyLoading || isPropertyUnitsLoading || isLoanStatusLoading}
        actions={getUnitFormActions(
          type,
          currentUnitIdx,
          unitIds.length,
          payoff,
          loanStatusData.loanSubStatus
        )}
        {...(handleClose ? { handleClose: handleClose } : {})}
      />
      {unitId && (
        <LmsTable
          skipClick
          title="Allocated Costs"
          saveSuffix="/loanproperty/allocated-costs"
          actionButtons={["export", "save", "cancel"]}
          myForm={allocatedCostsFields(new BCFormController())}
          dataRules={[
            { field: "allocatedPercent", rule: "format", display: "percent" },
            { field: "allocatedCost", rule: "format", display: "dollar" },
          ]}
          columnLabelsDictionary={{
            loanNumber: "Loan #",
            allocatedCost: "$ Assigned per Unit",
            allocatedPercent: "% Assigned per Unit",
            unitReconveyanceComments: "Reconveyance Comments",
            unitReconveyanceDate: "Reconveyance Date",
          }}
          apiSuffix={`loanproperty/units/${unitId}/allocated-costs`}
          calculatedColumns={["allocatedCost"]}
        />
      )}
    </>
  );
};

const handleFormChanged = async (options, field) => {
  const {
    inputs: { borrowerOrganizationId, specialPurposeEntityId, loanId },
    setAllDropdownOptionsData,
    setInputs,
    fetchData,
    setIsFormLoading,
  } = options;

  const setNewValues = (values) => setInputs((prevInputs) => ({ ...prevInputs, ...values }));
  const handleFetch = async (url: string, defaultData: unknown = null) => {
    try {
      setIsFormLoading(true);
      if (!url) return defaultData;
      const res = await fetchData(url);
      if (res.data) return res.data;
      return defaultData;
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleFetchDropdownOption = async (inputName: string, url: string) => {
    const options = getDropdownSemanticOptionsFromData(await handleFetch(url, []));
    setAllDropdownOptionsData((prevOptions) => ({
      ...prevOptions,
      [inputName]: options,
    }));
  };

  const setLoanOptions = (params = { borrowerOrganizationId, specialPurposeEntityId }) => {
    handleFetchDropdownOption("loanId", appendParamsToUrl(LOANS_URL, params));
  };

  switch (field) {
    case "borrowerOrganizationId": {
      setLoanOptions();
      setNewValues({ loanId: null, specialPurposeEntityId: null });
      handleFetchDropdownOption(
        "specialPurposeEntityId",
        borrowerOrganizationId ? `orgs/list-spes/${borrowerOrganizationId}` : ""
      );

      break;
    }
    case "specialPurposeEntityId": {
      setLoanOptions();
      setNewValues({ loanId: null });
      break;
    }
    case "loanId": {
      const defaultValues = {
        borrowerOrganizationId: null,
        specialPurposeEntityId: null,
      };

      if (!loanId) setLoanOptions(defaultValues);
      const data = await handleFetch(loanId ? `/loans/${loanId}/details` : "", defaultValues);
      setNewValues({
        loanPropertyId: null,
        borrowerOrganizationId: data.borrowerOrganizationId,
        specialPurposeEntityId: data.specialPurposeEntityId,
      });

      break;
    }
  }
};

const LmsServicingUnitsSelectLoanForm = () => {
  const myForm = new BCFormController();
  const history = useHistory();

  const handleContinue = (inputs) => {
    history.replace(`/servicing-units/add?${PARAMS.LOAN_PROPERTY_ID}=${inputs.loanPropertyId}`);
  };

  [
    myForm.getDropdownField("Borrower", "orgs/list-loans-borrower-org", {
      name: "borrowerOrganizationId",
      required: true,
    }),
    myForm.getDropdownField("Special Purpose Entity", []),
    myForm.getDropdownField("Loan", LOANS_URL, {
      required: true,
    }),
    myForm.getDropdownField("Loan Property", "loandetails/list-loan-properties", {
      required: true,
      dependencyField: "loanId",
    }),
  ].forEach((field) => myForm.addCenteredGroup([field]));

  return (
    <LmsFormGenerator
      myForm={myForm}
      handleSubmit={handleContinue}
      onFormChanged={handleFormChanged}
      handleSubmitValidation={submitValidationIsLoanLost}
      actions={[
        {
          text: "Next",
          action: "save",
        },
        "close",
      ]}
    />
  );
};

const ServicingUnitsTable = () => {
  return (
    <LmsTable
      apiSuffix="/loanproperty/units"
      deleteSuffix="/loanproperty/units"
      actionButtons={["add", "edit", "remove", "export", "save", "cancel"]}
      columnLabelsDictionary={propertyUnitTableColumnsDictionary}
      saveSuffix="loanproperty/units"
      myForm={loanPropertyUnitReconveyanceFields(new BCFormController())}
      columnClickOptions={{
        unitNumber: {
          idProp: "id",
          path: `${BASE_PATH}/edit/{id}`,
        },
      }}
    />
  );
};

const LmsServicingUnits = ({ params = {}, history }) => {
  const { isMainPage, isAdd, isEdit, isActionAllowed, id, isView, query } = useParseRouteActions(
    params,
    ["add", "edit"]
  );

  const [loanPropertyId, copyUnitId] = [PARAMS.LOAN_PROPERTY_ID, PARAMS.COPY_UNIT_ID].map((param) =>
    query.get(param)
  );

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <ServicingUnitsTable />}
      {(isAdd || isEdit || isView) &&
        (isAdd && !loanPropertyId ? (
          <LmsServicingUnitsSelectLoanForm />
        ) : (
          <LoanPropertyUnitReconveyanceForm
            unitId={id}
            copyUnitId={copyUnitId}
            loanPropertyId={loanPropertyId}
            type={isAdd ? "add" : isEdit ? "edit" : "view"}
          />
        ))}
    </>
  );
};

export default LmsServicingUnits;
